import React, { useEffect, useState } from "react";
import { Button, Modal, Select, Form, Input, message } from "antd";
import { CloseCircleOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { hasPermission } from "../store/userSlice";
import { cancelWarning } from "../api/warmings/warningRequest";
import { getMotifsWarning } from "../api/motifs-annulation/motifRequests";

const { TextArea } = Input;

export default function CancelWarning({ warningId, onCancelled }) {
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();
  const [reasons, setReasons] = useState([]);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const canCancel = useSelector((state) => hasPermission(state, "warnings", "cancel"));

  useEffect(() => {
    let mounted = true;
    const fetchReasons = async () => {
      if (!canCancel) return;
      setLoadingReasons(true);
      try {
        const data = await getMotifsWarning();
        if (mounted) setReasons(data);
      } catch (err) {
        console.error(err);
        message.error("Erreur lors du chargement des motifs d'annulation");
      } finally {
        if (mounted) setLoadingReasons(false);
      }
    };

    if (visible) fetchReasons();

    return () => {
      mounted = false;
    };
  }, [visible, canCancel]);

  if (!canCancel) return null;

  const showModal = () => setVisible(true);
  const handleCancel = () => {
    setVisible(false);
    form.resetFields();
  };

  const handleFinish = async (values) => {
    setIsCancelling(true);
    try {
      await cancelWarning(warningId, {
        cancellation_reason_id: values.cancellation_reason_id,
        cancellation_reason_description: values.cancellation_reason_description || "",
      });
      message.success("L'avertissement a été annulé avec succès");
      setVisible(false);
      form.resetFields();
      if (typeof onCancelled === "function") onCancelled();
    } catch (err) {
      console.error(err);
      message.error("Erreur lors de l'annulation de l'avertissement");
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <>
      <Button
        danger
        type="primary"
        icon={<CloseCircleOutlined />}
        onClick={showModal}
        className="hover:opacity-90 transition-all"
        style={{
          borderRadius: "6px",
          boxShadow: "0 2px 4px rgba(220, 38, 38, 0.2)",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        Annuler l'avertissement
      </Button>

      <Modal title="Annuler l'avertissement" open={visible} onCancel={handleCancel} footer={null} destroyOnClose>
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Form.Item
            name="cancellation_reason_id"
            label="Motif d'annulation"
            rules={[{ required: true, message: "Veuillez choisir un motif d'annulation" }]}
          >
            <Select placeholder="Sélectionnez un motif" loading={loadingReasons}>
              {reasons.map((r) => (
                <Select.Option key={r.id} value={r.id}>
                  {r.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="cancellation_reason_description" label="Description (optionnel)">
            <TextArea rows={4} placeholder="Entrez une description supplémentaire" />
          </Form.Item>

          <div className="flex justify-end gap-2">
            <Button onClick={handleCancel}>Annuler</Button>
            <Button type="primary" danger htmlType="submit" loading={isCancelling}>
              Confirmer l'annulation
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
}
